/**
 * Utility Functions
 * Common helper functions used across the application
 */

import { format, parseISO } from 'date-fns'
import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { ScopeOfWorkEnum } from '@/generated/graphql'
import { PDFDocument } from 'pdf-lib'
import { CONFIG } from '@/config'

/**
 * Combine and merge Tailwind CSS classes
 */
export const cn = (...inputs: ClassValue[]) => {
  return twMerge(clsx(inputs));
};

/**
 * Format date for display
 */
export const formatDate = (
  date: string | Date,
  formatString: string = 'dd/MM/yyyy'
): string => {
  if (!date) return 'Invalid date';
  
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    if (isNaN(dateObj.getTime())) return 'Invalid date';
    return format(dateObj, formatString);
  } catch {
    return 'Invalid date';
  }
};

/**
 * Format phone number for display (Indian format)
 */
export const formatPhoneNumber = (phoneNumber: string | null | undefined): string => {
  if (!phoneNumber?.trim()) return '';
  
  const digits = phoneNumber.replace(/\D/g, '');
  if (!digits) return phoneNumber;
  
  // Normalize to international format
  const normalizedNumber = (() => {
    switch (digits.length) {
      case 10: return `91${digits}`; // Add India country code
      case 11: return digits.startsWith('0') ? `91${digits.slice(1)}` : digits;
      case 12: return digits.startsWith('91') ? digits : null;
      default: return null;
    }
  })();
  
  // Format using template literals
  if (normalizedNumber?.length === 12 && normalizedNumber.startsWith('91')) {
    const [, countryCode, ...phoneDigits] = normalizedNumber.match(/^(\d{2})(\d{5})(\d{5})$/) || [];
    if (countryCode && phoneDigits.length === 2) {
      return `+${countryCode} ${phoneDigits.join(' ')}`;
    }
  }
  
  return phoneNumber;
};

/**
 * Generate Gujarati time format
 */
export const generateGujaratiTime = (time: string): string => {
  if (!time) return '';
  
  const [hours, minutes] = time.split(':').map(Number);
  let hour12 = hours;
  if (hour12 === 0) hour12 = 12;
  else if (hour12 > 12) hour12 = hour12 - 12;
  
  const timeIn12Hour = `${hour12}:${minutes.toString().padStart(2, '0')}`;
  const totalMinutes = (hours * 60) + minutes;
  
  // Apply Gujarati time periods
  if (totalMinutes >= 240 && totalMinutes < 660) {
    return `સવારે ${timeIn12Hour}`; // Morning
  } else if (totalMinutes >= 660 && totalMinutes < 1080) {
    return `બપોરે ${timeIn12Hour}`; // Afternoon  
  } else if (totalMinutes >= 1080 && totalMinutes < 1260) {
    return `સાંજે ${timeIn12Hour}`; // Evening
  } else if (totalMinutes >= 1260 || totalMinutes < 240) {
    return `રાતે ${timeIn12Hour}`; // Night
  } else {
    const period = hours >= 12 ? 'PM' : 'AM';
    return `${timeIn12Hour} ${period}`;
  }
};

/**
 * Format date range for display
 */
export const formatDateRange = (startDate: string, endDate: string): { start: string; end: string } => {
  if (!startDate && !endDate) return { start: '-', end: '' };
  if (!endDate) return { start: formatDate(startDate), end: '' };
  
  const startDateObj = new Date(startDate);
  const endDateObj = new Date(endDate);
  const isSameDay = startDateObj.toDateString() === endDateObj.toDateString();
  
  const start = formatDate(startDate);
  if (isSameDay) return { start, end: '' };
  
  const end = formatDate(endDate);
  return { start, end: `to ${end}` };
};

/**
 * Format date short (alias for formatDate)
 */
export const formatDateShort = (dateString: string): string => {
  return formatDate(dateString);
};

/**
 * Format event date as dd/MM/yy (e.g. 15/03/25)
 */
export const formatEventDate = (dateString: string): string => {
  if (!dateString) return '-'
  const date = parseISO(dateString)
  return isNaN(date.getTime()) ? '-' : format(date, 'dd/MM/yy')
};

/**
 * Format date long
 */
export const formatDateLong = (dateString: string): string => {
  if (!dateString) return '';
  const date = parseISO(dateString);
  if (isNaN(date.getTime())) return '';
  return format(date, 'd MMM yyyy');
};


/**
 * Generate initials from name
 */
export const getInitials = (name: string): string => {
  return name
    .split(' ')
    .map(part => part.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

/**
 * Debounce function calls
 */
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): T => {
  let timeout: ReturnType<typeof setTimeout>;
  return ((...args: any[]) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(null, args), wait);
  }) as T;
};

/**
 * Generate random ID
 */
export const generateId = (): string => {
  return Math.random().toString(36).substring(2, 11);
};

/**
 * Capitalize first letter
 */
export const capitalize = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * Truncate text with ellipsis
 */
export const truncate = (str: string, length: number): string => {
  if (str.length <= length) return str;
  return str.slice(0, length) + '...';
};

/**
 * Human-readable label for ScopeOfWorkEnum values
 */
export const SCOPE_OF_WORK_LABELS: Record<ScopeOfWorkEnum, string> = {
  [ScopeOfWorkEnum.Labour]: 'Labour',
  [ScopeOfWorkEnum.WithMaterials]: 'With materials',
  [ScopeOfWorkEnum.SupplyOnly]: 'Supply only',
  [ScopeOfWorkEnum.ServiceOnly]: 'Service only',
}

export const scopeOfWorkLabel = (value: ScopeOfWorkEnum | null | undefined): string =>
  value ? (SCOPE_OF_WORK_LABELS[value] ?? value) : ''

const MATERIAL_SCOPES = new Set([ScopeOfWorkEnum.Labour, ScopeOfWorkEnum.SupplyOnly])

export const scopeRequiresMaterials = (scope: ScopeOfWorkEnum | string | null | undefined): boolean =>
  !!scope && MATERIAL_SCOPES.has(scope as ScopeOfWorkEnum)

/**
 * Download PDF from a given path by constructing full URL with API base
 */
export const downloadPDFFromPath = async (path: string): Promise<ArrayBuffer> => {
  try {
    // Construct full URL by combining API base URL with path
    const fullUrl = `${CONFIG.API.url}${path}`
    console.log('📥 Downloading PDF from:', fullUrl)
    
    const headers: Record<string, string> = {}
    if (window.location.hostname === 'localhost') {
      headers['x-client-domain'] = CONFIG.CLIENT.domain
    }
    const response = await fetch(fullUrl, { headers })
    if (!response.ok) {
      throw new Error(`Failed to download PDF from ${fullUrl}: ${response.statusText}`)
    }
    return await response.arrayBuffer()
  } catch (error) {
    console.error('Error downloading PDF:', error)
    throw error
  }
}

/**
 * Open PDF in new tab by downloading from path
 */
export const openPDFFromPath = async (path: string, filename?: string): Promise<void> => {
  try {
    const arrayBuffer = await downloadPDFFromPath(path)
    const blob = new Blob([arrayBuffer], { type: 'application/pdf' })
    const url = URL.createObjectURL(blob)
    
    // Open in new tab
    const newWindow = window.open(url, '_blank')
    if (newWindow && filename) {
      // Set the title to filename if available
      newWindow.document.title = filename
    }
    
    // Clean up blob URL after 30 seconds to free memory
    // This gives enough time for even large PDFs to load in the new tab
    setTimeout(() => {
      URL.revokeObjectURL(url)
      console.log('🧹 Cleaned up blob URL for PDF')
    }, 30000)
  } catch (error) {
    console.error('Error opening PDF:', error)
    throw error
  }
}

/**
 * Download a PDF from a given path and force a browser download using the
 * backend-provided filename.
 */
export const downloadPDFFromPathAs = async (path: string, filename: string): Promise<void> => {
  try {
    const arrayBuffer = await downloadPDFFromPath(path)
    const blob = new Blob([arrayBuffer], { type: 'application/pdf' })
    const url = URL.createObjectURL(blob)

    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()

    setTimeout(() => URL.revokeObjectURL(url), 30000)
  } catch (error) {
    console.error('Error downloading PDF:', error)
    throw error
  }
}

/**
 * Merge multiple PDFs into a single PDF and open in new tab
 */
export const mergePDFsAndOpen = async (pdfEvents: Array<{ path: string; filename: string }>): Promise<void> => {
  try {
    if (pdfEvents.length === 0) {
      throw new Error('No PDFs to merge')
    }

    // Create a new PDF document
    const mergedPdf = await PDFDocument.create()

    for (const pdfEvent of pdfEvents) {
      try {
        // Download PDF from path
        const pdfBytes = await downloadPDFFromPath(pdfEvent.path)
        const pdf = await PDFDocument.load(pdfBytes)

        const copiedPages = await mergedPdf.copyPages(pdf, pdf.getPageIndices())
        copiedPages.forEach((page) => mergedPdf.addPage(page))
      } catch (error) {
        console.warn(`Failed to process PDF ${pdfEvent.filename}:`, error)
        // Continue with other PDFs even if one fails
      }
    }

    // Final merged PDF
    const mergedPdfBytes = await mergedPdf.save()

    // Convert to Blob and open in new tab
    const blob = new Blob([mergedPdfBytes as any], { type: 'application/pdf' })
    const url = URL.createObjectURL(blob)
    window.open(url, '_blank')
    
    // Clean up blob URL after 30 seconds to free memory
    setTimeout(() => {
      URL.revokeObjectURL(url)
      console.log('🧹 Cleaned up blob URL for merged PDF')
    }, 30000)
    
  } catch (error) {
    console.error('Error merging PDFs:', error)
    throw error
  }
}

